import { useReducer, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ManagedLineItem,
  LineItemAction,
  LineItemStatus,
  LineItemValidation,
  LineItemConfig,
  LineItemSummary,
  LineItemVariant
} from '../types/lineItemTypes';
import { PurchaseOrderLineItem, ProductVariant } from '../types/api';
import { VAT_RATE, MISSING_PRODUCT_IMAGE_URL } from '../config';
import * as _ from 'lodash';

const defaultConfig: LineItemConfig = {
  allowDuplicates: false,
  minQuantity: 1,
  validateReceipts: true
};

// Reducer for managing line item state
function lineItemReducer(state: ManagedLineItem[], action: LineItemAction): ManagedLineItem[] {
  switch (action.type) {
    case 'SET_ITEMS':
      // Convert existing line items to managed items and assign sequences if missing
      return action.payload.items.map((item, index) => ({
        ...item,
        tempId: item.guid || uuidv4(),
        status: LineItemStatus.ACTIVE,
        sequence: item.sequence ?? (index + 1), // Assign sequence if missing
      }));

    case 'MERGE_SERVER_ITEMS': {
      // Reconcile server-assigned IDs back into local state after an autosave.
      // For every active item that doesn't yet have a real (positive) database id,
      // find the matching server row by variantSku and adopt its id and guid ONLY.
      // We deliberately leave quantity/cost/sequence/etc. untouched so that any edits
      // the user made while the save was in flight are preserved. Without this, the
      // next autosave would resend these items with no id and the server would treat
      // them as new rows, duplicating every manually-added line item.
      const serverItems = action.payload.items || [];
      let changed = false;
      const merged = state.map(item => {
        if (item.status !== LineItemStatus.ACTIVE) return item;
        if (item.id && item.id > 0) return item; // already reconciled

        const match = serverItems.find(si => si.variantSku === item.variantSku);
        if (!match || !match.id) return item;

        changed = true;
        return { ...item, id: match.id, guid: match.guid ?? item.guid };
      });
      // Return the original reference when nothing changed so React bails out of the
      // re-render. Otherwise the autosave effect (which depends on lineItems) would
      // reschedule on every merge and loop indefinitely.
      return changed ? merged : state;
    }

    case 'ADD_ITEM': {
      const { variant, quantity } = action.payload;
      const tempId = uuidv4();

      // Calculate the next sequence number
      const maxSequence = state
        .filter(item => item.sequence != null)
        .reduce((max, item) => Math.max(max, item.sequence || 0), 0);

      const poliDescription = variant.product
        ? `${variant.product.name} ${
            variant.filterValues ? "(" + variant.filterValues + ")" : ""
          } ${
            variant.boughtInPacks === true ? "(Pack of " + variant.packSize + ")" : ""
          }`
        : variant.description || `${variant.sku} - Special Product`;

      const newItem: ManagedLineItem = {
        tempId,
        status: LineItemStatus.ACTIVE,
        product: variant.product,
        description: poliDescription,
        variantSku: variant.sku,
        variantGuid: variant.guid,
        variantUPC: variant.barcode,
        variantSupplierCode: variant.supplierCode,
        variantTaxRate: (variant.product?.taxable ?? variant.taxable) ? VAT_RATE : 0,
        stockOnHand: variant.stockOnHand,
        variantMediaURI: !_.isEmpty(variant.imageUrls) && variant.imageUrls[0]
          ? variant.imageUrls[0]
          : MISSING_PRODUCT_IMAGE_URL,
        itemCost: variant.buyPrice,
        quantityOrdered: quantity,
        packSize: variant.packSize || 1,
        sequence: maxSequence + 1,
        receipts: [],
      };

      return [...state, newItem];
    }

    case 'REMOVE_ITEM': {
      const { tempId } = action.payload;
      return state.map(item => {
        if (item.tempId === tempId) {
          // If item has a real ID (persisted), mark for deletion
          // Otherwise just mark as deleted for removal
          return {
            ...item,
            status: item.id ? LineItemStatus.PENDING_DELETE : LineItemStatus.DELETED,
            originalItem: item.status === LineItemStatus.ACTIVE ? { ...item } : item.originalItem
          };
        }
        return item;
      });
    }

    case 'RESTORE_ITEM': {
      const { tempId } = action.payload;
      return state.map(item => {
        if (item.tempId === tempId && item.status === LineItemStatus.PENDING_DELETE) {
          const restored = item.originalItem ? { ...item.originalItem } : item;
          return {
            ...restored,
            tempId: item.tempId,
            status: LineItemStatus.ACTIVE,
            originalItem: undefined
          };
        }
        return item;
      });
    }

    case 'UPDATE_QUANTITY': {
      const { tempId, quantity } = action.payload;
      return state.map(item => {
        if (item.tempId === tempId) {
          return {
            ...item,
            quantityOrdered: Math.max(quantity, 1)
          };
        }
        return item;
      });
    }

    case 'UPDATE_FIELD': {
      const { tempId, field, value } = action.payload;
      return state.map(item => {
        if (item.tempId === tempId) {
          return {
            ...item,
            [field]: value
          };
        }
        return item;
      });
    }

    case 'CLEAR_DELETED':
      return state.filter(item => item.status !== LineItemStatus.DELETED);

    case 'MOVE_ITEM_UP': {
      const { tempId } = action.payload;
      const activeItems = state.filter(item => item.status === LineItemStatus.ACTIVE);

      // Sort by sequence to ensure proper order
      activeItems.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      const currentIndex = activeItems.findIndex(item => item.tempId === tempId);
      if (currentIndex <= 0) return state; // Already at top or not found

      // Swap sequences with the item above
      const currentItem = activeItems[currentIndex];
      const aboveItem = activeItems[currentIndex - 1];

      return state.map(item => {
        if (item.tempId === currentItem.tempId) {
          return { ...item, sequence: aboveItem.sequence };
        }
        if (item.tempId === aboveItem.tempId) {
          return { ...item, sequence: currentItem.sequence };
        }
        return item;
      });
    }

    case 'MOVE_ITEM_DOWN': {
      const { tempId } = action.payload;
      const activeItems = state.filter(item => item.status === LineItemStatus.ACTIVE);

      // Sort by sequence to ensure proper order
      activeItems.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      const currentIndex = activeItems.findIndex(item => item.tempId === tempId);
      if (currentIndex < 0 || currentIndex >= activeItems.length - 1) return state; // Already at bottom or not found

      // Swap sequences with the item below
      const currentItem = activeItems[currentIndex];
      const belowItem = activeItems[currentIndex + 1];

      return state.map(item => {
        if (item.tempId === currentItem.tempId) {
          return { ...item, sequence: belowItem.sequence };
        }
        if (item.tempId === belowItem.tempId) {
          return { ...item, sequence: currentItem.sequence };
        }
        return item;
      });
    }

    default:
      return state;
  }
}

export function useLineItems(initialItems: PurchaseOrderLineItem[] = [], config: Partial<LineItemConfig> = {}) {
  const mergedConfig = { ...defaultConfig, ...config };
  const [items, dispatch] = useReducer(lineItemReducer, [], () =>
    initialItems.map((item, index) => ({
      ...item,
      tempId: item.guid || uuidv4(),
      status: LineItemStatus.ACTIVE,
      sequence: item.sequence ?? (index + 1), // Assign sequence if missing
    }))
  );

  // Validation function
  const validateAddItem = useCallback((variant: ProductVariant, quantity: number): LineItemValidation => {
    const errors: string[] = [];

    if (!variant) {
      errors.push('Product variant is required');
    }

    if (quantity < mergedConfig.minQuantity) {
      errors.push(`Quantity must be at least ${mergedConfig.minQuantity}`);
    }

    if (!mergedConfig.allowDuplicates) {
      const existingActive = items.find(item =>
        item.variantSku === variant.sku &&
        item.status === LineItemStatus.ACTIVE
      );
      if (existingActive) {
        errors.push(`${variant.sku} is already in the order. Update the existing quantity instead.`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [items, mergedConfig]);

  const validateUpdateQuantity = useCallback((tempId: string, quantity: number): LineItemValidation => {
    const errors: string[] = [];
    const item = items.find(i => i.tempId === tempId);

    if (!item) {
      errors.push('Item not found');
      return { isValid: false, errors };
    }

    if (quantity < mergedConfig.minQuantity) {
      errors.push(`Quantity must be at least ${mergedConfig.minQuantity}`);
    }

    if (mergedConfig.validateReceipts && item.receipts?.length > 0) {
      const totalReceived = item.receipts.reduce((sum, receipt) => sum + receipt.received, 0);
      if (quantity < totalReceived) {
        errors.push(`Cannot reduce quantity below ${totalReceived} (already received)`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [items, mergedConfig]);

  // Action creators
  const addItem = useCallback((variant: LineItemVariant, quantity: number = 1) => {
    const validation = validateAddItem(variant, quantity);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    // Check for existing pending delete item to restore
    const existingDeleted = items.find(item =>
      item.variantSku === variant.sku &&
      item.status === LineItemStatus.PENDING_DELETE
    );

    if (existingDeleted) {
      dispatch({ type: 'RESTORE_ITEM', payload: { tempId: existingDeleted.tempId } });
      dispatch({ type: 'UPDATE_QUANTITY', payload: { tempId: existingDeleted.tempId, quantity } });
    } else {
      // Check for existing active item to update quantity
      const existingActive = items.find(item =>
        item.variantSku === variant.sku &&
        item.status === LineItemStatus.ACTIVE
      );

      if (existingActive && mergedConfig.allowDuplicates) {
        dispatch({
          type: 'UPDATE_QUANTITY',
          payload: {
            tempId: existingActive.tempId,
            quantity: existingActive.quantityOrdered + quantity
          }
        });
      } else {
        dispatch({ type: 'ADD_ITEM', payload: { variant, quantity } });
      }
    }
  }, [items, validateAddItem, mergedConfig.allowDuplicates]);

  const removeItem = useCallback((tempId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { tempId } });
  }, []);

  const restoreItem = useCallback((tempId: string) => {
    dispatch({ type: 'RESTORE_ITEM', payload: { tempId } });
  }, []);

  const updateQuantity = useCallback((tempId: string, quantity: number) => {
    const validation = validateUpdateQuantity(tempId, quantity);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }
    dispatch({ type: 'UPDATE_QUANTITY', payload: { tempId, quantity } });
  }, [validateUpdateQuantity]);

  const updateField = useCallback((tempId: string, field: string, value: any) => {
    dispatch({ type: 'UPDATE_FIELD', payload: { tempId, field, value } });
  }, []);

  const clearDeleted = useCallback(() => {
    dispatch({ type: 'CLEAR_DELETED' });
  }, []);

  const setItems = useCallback((newItems: PurchaseOrderLineItem[]) => {
    dispatch({ type: 'SET_ITEMS', payload: { items: newItems } });
  }, []);

  const mergeServerItems = useCallback((serverItems: PurchaseOrderLineItem[]) => {
    dispatch({ type: 'MERGE_SERVER_ITEMS', payload: { items: serverItems } });
  }, []);

  const moveItemUp = useCallback((tempId: string) => {
    dispatch({ type: 'MOVE_ITEM_UP', payload: { tempId } });
  }, []);

  const moveItemDown = useCallback((tempId: string) => {
    dispatch({ type: 'MOVE_ITEM_DOWN', payload: { tempId } });
  }, []);

  // Computed values
  const summary: LineItemSummary = useMemo(() => {
    const activeItems = items.filter(item => item.status === LineItemStatus.ACTIVE);

    const subTotal = activeItems.reduce((sum, item) =>
      sum + (item.itemCost * item.quantityOrdered), 0
    );

    const taxAmount = activeItems.reduce((sum, item) =>
      sum + (item.variantTaxRate ?
        (item.variantTaxRate / 100) * item.itemCost * item.quantityOrdered : 0), 0
    );

    return {
      activeItems,
      subTotal: Math.round(subTotal * 100) / 100, // Round to 2 decimal places
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round((subTotal + taxAmount) * 100) / 100,
      itemCount: activeItems.length
    };
  }, [items]);

  const visibleItems = useMemo(() =>
    items.filter(item => item.status !== LineItemStatus.DELETED)
         .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    [items]
  );

  const activeItems = useMemo(() =>
    items.filter(item => item.status === LineItemStatus.ACTIVE)
         .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    [items]
  );

  const pendingDeleteItems = useMemo(() =>
    items.filter(item => item.status === LineItemStatus.PENDING_DELETE),
    [items]
  );

  // Get clean items for server submission (remove temp fields)
  const getCleanItems = useCallback((): PurchaseOrderLineItem[] => {
    // Include active items and items pending deletion (for server to process deletion)
    const itemsToSubmit = items.filter(item => 
      item.status === LineItemStatus.ACTIVE || item.status === LineItemStatus.PENDING_DELETE
    );

    return itemsToSubmit.map(item => {
      const { tempId, status, originalItem, ...cleanItem } = item;

      // Remove temporary negative ID if it exists
      if (cleanItem.id && cleanItem.id < 0) {
        delete cleanItem.id;
      }

      // Remove temp fields added for display
      delete cleanItem.stockOnHand;

      // Mark items for deletion with pendingDelete flag
      if (status === LineItemStatus.PENDING_DELETE) {
        cleanItem.pendingDelete = true;
      }

      return cleanItem;
    });
  }, [items]);

  return {
    items: visibleItems,
    activeItems,
    pendingDeleteItems,
    summary,
    addItem,
    removeItem,
    restoreItem,
    updateQuantity,
    updateField,
    clearDeleted,
    setItems,
    mergeServerItems,
    moveItemUp,
    moveItemDown,
    getCleanItems,
    validateAddItem,
    validateUpdateQuantity
  };
}